
import {defineComponent, PropType} from "vue";
import {CodeDescriptionRestDto, OverlayRestDto} from "@/api/pharma-cms-content-block/models";
import {OverlayPositionTypeEnum} from "@/api/enums/overlay-position-type-enum";
import {OverlayAnimationTypeEnum} from "@/api/enums/overlay-animation-type-enum";
import {OverlayTriggerTypeEnum} from "@/api/enums/overlay-trigger-type-enum";
import {CardinalDirectionEnum} from "@/api/enums/cardinal-direction-enum";
import {OverlayRestDtoModel} from "@/models/api/pharma-cms-content-block/OverlayRestDtoModel";
import {OverlayAnimationRestDtoModel} from "@/models/api/pharma-cms-content-block/OverlayAnimationRestDtoModel";
import {PaddingRestDtoModel} from "@/models/api/pharma-cms-content-page/PaddingRestDtoModel";
import {ComponentMixin} from "@/components/base/ComponentMixin";
import {EnumRestService} from "@/services/rest/enum/EnumRestService";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";
import GenericComponentWrapper from "@/components/base/GenericComponentWrapper.vue";
import AdvancedColorEditor from "@/components/layouts/content-pages/wysiwyg/misc/AdvancedColorEditor.vue";
import CssDimensionEditor from "@/components/layouts/content-pages/wysiwyg/misc/CssDimensionEditor.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputNumber from "@/components/UI/InputNumber.vue";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import Subtitle from "@/components/UI/Subtitle.vue";

export default defineComponent({
  name: "ImageOverlayDetail",
  mixins: [ComponentMixin],
  components: {
    GenericComponentWrapper,
    AdvancedColorEditor,
    CssDimensionEditor,
    InputSelect,
    InputNumber,
    InputTextEditor,
    Subtitle,
  },
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Object as PropType<OverlayRestDto>,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    errors: {
      type: Object,
      required: true,
    },
  },
  mounted() {
    this.initialize();
  },
  data() {
    return {
      localDto: OverlayRestDtoModel.createWithDefaults() as OverlayRestDto,
      // Always present so the inputs can bind to it; localDto.padding stays null
      // until a side is filled, so an untouched group is not sent.
      paddingDraft: this.modelValue.padding
        ? PaddingRestDtoModel.cloneFrom(this.modelValue.padding)
        : PaddingRestDtoModel.createWithDefaults(),
      // Always present so the inputs can bind to it; localDto.animation stays null
      // until a field is filled, so an untouched animation is not sent.
      animationDraft: this.modelValue.animation
        ? OverlayAnimationRestDtoModel.cloneFrom(this.modelValue.animation)
        : OverlayAnimationRestDtoModel.createEmpty(),
      enumBlendModeCode: [] as CodeDescriptionRestDto[],
      enumPositionTypeCode: [] as CodeDescriptionRestDto[],
      enumVerticalPositionCode: [] as CodeDescriptionRestDto[],
      enumAnimationTypeCode: [] as CodeDescriptionRestDto[],
      enumAnimationTriggerCode: [] as CodeDescriptionRestDto[],
      enumSlideDirectionCode: [] as CodeDescriptionRestDto[],
    };
  },
  watch: {
    modelValue: {
      immediate: true,
      handler(newValue: OverlayRestDto) {
        this.localDto = OverlayRestDtoModel.cloneFrom(newValue);
        this.paddingDraft = newValue.padding
          ? PaddingRestDtoModel.cloneFrom(newValue.padding)
          : PaddingRestDtoModel.createWithDefaults();
        this.animationDraft = newValue.animation
          ? OverlayAnimationRestDtoModel.cloneFrom(newValue.animation)
          : OverlayAnimationRestDtoModel.createEmpty();
      },
    },
  },
  computed: {
    showInsets(): boolean {
      return this.localDto.position.position_type_code !== OverlayPositionTypeEnum.STATIC;
    },
    showSlideDirection(): boolean {
      return this.animationDraft.animation_code === OverlayAnimationTypeEnum.SLIDE;
    },
    // An animation needs all three of its fields, so filling in one makes the others mandatory.
    // While they are all empty, there simply is no animation and nothing to complete.
    hasAnimationValue(): boolean {
      return !!(this.animationDraft.animation_code
        || this.animationDraft.animation_trigger_code
        || this.animationDraft.animation_duration_in_ms);
    },
    animationRules(): string | undefined {
      return this.hasAnimationValue ? 'required' : undefined;
    },
  },
  methods: {
    async doInitialize(): Promise<void> {
      [
        this.enumBlendModeCode,
        this.enumVerticalPositionCode,
      ] = await Promise.all([
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OVERLAY_BLEND_MODE, DpModuleEnum.CMS),
        EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.OBJECT_VERTICAL_POSITION, DpModuleEnum.CMS),
      ]);
      this.enumPositionTypeCode = [
        {code: OverlayPositionTypeEnum.STATIC, description: 'static'},
        {code: OverlayPositionTypeEnum.ABSOLUTE, description: 'absolute'},
        {code: OverlayPositionTypeEnum.RELATIVE, description: 'relative'},
        {code: OverlayPositionTypeEnum.FIXED, description: 'fixed'},
        {code: OverlayPositionTypeEnum.STICKY, description: 'sticky'},
      ];
      // Not served by the enum endpoint, so the option lists are built here —
      // same approach as the position type above and as ImagePositioningDetail.
      this.enumAnimationTypeCode = [
        {code: OverlayAnimationTypeEnum.FADE, description: this.$t('cms.overlayAnimationFade')},
        {code: OverlayAnimationTypeEnum.SLIDE, description: this.$t('cms.overlayAnimationSlide')},
        {code: OverlayAnimationTypeEnum.ZOOM, description: this.$t('cms.overlayAnimationZoom')},
      ];
      this.enumAnimationTriggerCode = [
        {code: OverlayTriggerTypeEnum.HOVER, description: this.$t('cms.overlayTriggerHover')},
      ];
      this.enumSlideDirectionCode = [
        {code: CardinalDirectionEnum.TO_TOP, description: this.$t('top')},
        {code: CardinalDirectionEnum.TO_BOTTOM, description: this.$t('bottom')},
        {code: CardinalDirectionEnum.TO_LEFT, description: this.$t('left')},
        {code: CardinalDirectionEnum.TO_RIGHT, description: this.$t('right')},
      ];
    },
    onPaddingChanged(): void {
      const draft = this.paddingDraft;
      const hasAnySide = !!(draft.top || draft.right || draft.bottom || draft.left);

      this.localDto.padding = hasAnySide ? draft : null;

      this.emitModelChanged();
    },
    onAnimationTypeChanged(): void {
      // A slide direction is only meaningful for a sliding animation.
      this.animationDraft.slide_direction_code = this.showSlideDirection
        ? this.animationDraft.slide_direction_code ?? CardinalDirectionEnum.TO_TOP
        : null;

      this.onAnimationChanged();
    },
    onAnimationChanged(): void {
      // An emptied duration arrives as NaN, which reads as a filled-in field.
      if (!Number.isFinite(this.animationDraft.animation_duration_in_ms)) {
        this.animationDraft.animation_duration_in_ms = null as unknown as number;
      }

      this.localDto.animation = this.hasAnimationValue ? this.animationDraft : null;

      this.emitModelChanged();
    },
    onPositionTypeChanged(): void {
      if (this.localDto.position.position_type_code === OverlayPositionTypeEnum.STATIC) {
        this.localDto.position.top = null;
        this.localDto.position.right = null;
        this.localDto.position.bottom = null;
        this.localDto.position.left = null;
      }
      this.emitModelChanged();
    },
    emitModelChanged(): void {
      this.$emit('update:modelValue', this.localDto);
    },
  },
});
